import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import MediaAPI from '../../../api/media';
import { constructMediaUrl } from '../../../api/helpers';
import { formatAxiosError } from '../../../api/httpError';
import Modal from '../../modals/Modal';
import { Button, Input } from '../../ui';
import { resolveMediaPreviewCandidates } from '../mediaPreview';

interface CropRect { x: number; y: number; width: number; height: number }
type AspectPreset = 'free' | '1:1' | '4:5' | '16:9';

interface CropImageModalProps {
  item: MediaLibraryItem;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (item: MediaLibraryItem) => void | Promise<void>;
}

const aspectValues: Record<Exclude<AspectPreset, 'free'>, number> = {
  '1:1': 1,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const largestRectForAspect = (
  imageWidth: number,
  imageHeight: number,
  targetAspect: number
): CropRect => {
  const imageAspect = imageWidth / imageHeight;
  if (imageAspect > targetAspect) {
    const width = targetAspect / imageAspect;
    return { x: (1 - width) / 2, y: 0, width, height: 1 };
  }
  const height = imageAspect / targetAspect;
  return { x: 0, y: (1 - height) / 2, width: 1, height };
};

const cropFileName = (name: string): string => {
  const trimmed = name.trim();
  const base = trimmed.replace(/\.[^.]+$/, '') || 'image';
  return `${base}-cropped`;
};

const pngFileName = (name: string): string => {
  const trimmed = name.trim() || 'image-cropped';
  return trimmed.toLowerCase().endsWith('.png') ? trimmed : `${trimmed}.png`;
};

const cropRouteUnavailable = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
  return status === 404 || status === 405;
};

/** Render the normalized crop locally when a rolling backend lacks the crop route. */
export const renderCropFile = (
  image: HTMLImageElement,
  rect: CropRect,
  name: string
): Promise<File> => {
  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  const x0 = Math.floor(rect.x * sourceWidth);
  const y0 = Math.floor(rect.y * sourceHeight);
  const x1 = Math.min(sourceWidth, Math.ceil((rect.x + rect.width) * sourceWidth));
  const y1 = Math.min(sourceHeight, Math.ceil((rect.y + rect.height) * sourceHeight));
  const width = Math.max(1, x1 - x0);
  const height = Math.max(1, y1 - y0);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return Promise.reject(new Error('This browser cannot prepare the cropped image.'));
  try {
    context.drawImage(image, x0, y0, width, height, 0, 0, width, height);
  } catch {
    return Promise.reject(new Error('The source image could not be read for local cropping.'));
  }
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('The browser could not encode the cropped image.'));
          return;
        }
        resolve(new File([blob], pngFileName(name), { type: 'image/png' }));
      }, 'image/png');
    } catch {
      reject(new Error('The source image blocks local cropping. Please retry after the backend update.'));
    }
  });
};

const CropImageModal: React.FC<CropImageModalProps> = ({ item, isOpen, onClose, onSaved }) => {
  const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
  const [rect, setRect] = React.useState<CropRect>({ x: 0, y: 0, width: 1, height: 1 });
  const [aspect, setAspect] = React.useState<AspectPreset>('free');
  const [name, setName] = React.useState(cropFileName(item.name || item.media_id || item.id));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [usedFallback, setUsedFallback] = React.useState(false);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const { image } = resolveMediaPreviewCandidates(item);
  // Preview the same original bytes the server crops. Thumbnail-first preview
  // candidates are ideal for grids but would make the crop rectangle misleading.
  const imageUrl = item.url
    ? constructMediaUrl(item.url)
    : image[0]
    ? constructMediaUrl(image[0])
    : '';
  const mediaId = item.media_id || item.id;

  React.useEffect(() => {
    if (!isOpen) return;
    setRect({ x: 0, y: 0, width: 1, height: 1 });
    setAspect('free');
    setName(cropFileName(item.name || mediaId));
    setError('');
    setUsedFallback(false);
  }, [isOpen, item.name, mediaId]);

  const setPosition = (axis: 'x' | 'y', percent: number) => {
    setRect((current) => {
      const room = axis === 'x' ? 1 - current.width : 1 - current.height;
      return { ...current, [axis]: room * (percent / 100) };
    });
  };

  const applyAspect = (next: AspectPreset) => {
    setAspect(next);
    if (next === 'free' || !imageSize.width || !imageSize.height) return;
    setRect(largestRectForAspect(imageSize.width, imageSize.height, aspectValues[next]));
  };

  const setScale = (percent: number) => {
    if (aspect === 'free' || !imageSize.width || !imageSize.height) return;
    const maximum = largestRectForAspect(imageSize.width, imageSize.height, aspectValues[aspect]);
    const scale = clamp(percent / 100, 0.1, 1);
    const width = maximum.width * scale;
    const height = maximum.height * scale;
    setRect((current) => ({
      x: clamp(current.x + (current.width - width) / 2, 0, 1 - width),
      y: clamp(current.y + (current.height - height) / 2, 0, 1 - height),
      width,
      height,
    }));
  };

  const setFreeSize = (axis: 'width' | 'height', percent: number) => {
    const value = clamp(percent / 100, 0.05, 1);
    setRect((current) => ({
      ...current,
      [axis]: value,
      x: axis === 'width' ? clamp(current.x, 0, 1 - value) : current.x,
      y: axis === 'height' ? clamp(current.y, 0, 1 - value) : current.y,
    }));
  };

  const setOutputPixels = (axis: 'width' | 'height', pixels: number) => {
    if (!imageSize.width || !imageSize.height || !Number.isFinite(pixels)) return;
    const sourcePixels = axis === 'width' ? imageSize.width : imageSize.height;
    if (aspect === 'free') {
      setFreeSize(axis, (clamp(pixels, 1, sourcePixels) / sourcePixels) * 100);
      return;
    }
    const maxRect = largestRectForAspect(imageSize.width, imageSize.height, aspectValues[aspect]);
    const maxPixels = sourcePixels * maxRect[axis];
    setScale((clamp(pixels, 1, maxPixels) / maxPixels) * 100);
  };

  const resetCrop = () => {
    setAspect('free');
    setRect({ x: 0, y: 0, width: 1, height: 1 });
    setError('');
  };

  const handleSave = async () => {
    if (saving || !imageUrl) return;
    setSaving(true);
    setError('');
    setUsedFallback(false);
    try {
      let cropped: MediaLibraryItem;
      try {
        cropped = await MediaAPI.cropMediaImage(mediaId, { ...rect, name: name.trim() || undefined });
      } catch (caught) {
        if (!cropRouteUnavailable(caught) || !imageRef.current) throw caught;
        const file = await renderCropFile(imageRef.current, rect, name);
        cropped = await MediaAPI.uploadMediaLibraryFile(file, { type: 'image', source: 'edited_crop' });
        setUsedFallback(true);
      }
      await onSaved?.(cropped);
      onClose();
    } catch (caught) {
      setError(formatAxiosError(caught, 'Could not save the cropped copy.'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const size = {
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    };
    setImageSize(size);
    if (aspect !== 'free' && size.width > 0 && size.height > 0) {
      setRect(largestRectForAspect(size.width, size.height, aspectValues[aspect]));
    }
  };

  const horizontalPosition = rect.width >= 1 ? 0 : (rect.x / (1 - rect.width)) * 100;
  const verticalPosition = rect.height >= 1 ? 0 : (rect.y / (1 - rect.height)) * 100;
  const maximum = aspect !== 'free' && imageSize.width && imageSize.height
    ? largestRectForAspect(imageSize.width, imageSize.height, aspectValues[aspect])
    : null;
  const scale = maximum ? (rect.width / maximum.width) * 100 : 100;
  const outputWidth = Math.max(1, Math.round(imageSize.width * rect.width));
  const outputHeight = Math.max(1, Math.round(imageSize.height * rect.height));

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} title="Crop image" size="lg">
      <p className="text-xs text-slate-400">
        The original stays unchanged. Saving creates a new PNG copy linked to this image.
      </p>

      {imageUrl ? (
        <div className="flex justify-center rounded border border-white/15 bg-black/50 p-2 overflow-auto">
          <div className="relative inline-block max-w-full">
            <img
              ref={imageRef}
              src={imageUrl}
              alt={item.name || 'Image to crop'}
              className="block max-h-[48vh] max-w-full object-contain"
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              <div
                className="absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.58)]"
                style={{
                  left: `${rect.x * 100}%`, top: `${rect.y * 100}%`,
                  width: `${rect.width * 100}%`, height: `${rect.height * 100}%`,
                }}
              >
                <span className="absolute inset-x-1/3 top-0 bottom-0 border-x border-white/35" />
                <span className="absolute inset-y-1/3 left-0 right-0 border-y border-white/35" />
              </div>
            </div>
          </div>
        </div>
      ) : <p className="text-xs text-red-400" role="alert">This image has no crop-ready source URL.</p>}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Aspect ratio</p>
            <div className="flex flex-wrap gap-1.5">
              {(['free', '1:1', '4:5', '16:9'] as AspectPreset[]).map((preset) => (
                <Button key={preset} type="button" size="sm" variant={aspect === preset ? 'secondary' : 'ghost'} onClick={() => applyAspect(preset)}>
                  {preset === 'free' ? 'Free' : preset}
                </Button>
              ))}
              <Button type="button" size="sm" variant="ghost" onClick={resetCrop} disabled={saving}>
                Reset
              </Button>
            </div>
          </div>
          {aspect === 'free' ? (
            <>
              <label className="block text-xs text-slate-300">Width {Math.round(rect.width * 100)}%
                <input className="w-full" type="range" min="5" max="100" value={Math.round(rect.width * 100)} onChange={(e) => setFreeSize('width', Number(e.target.value))} />
              </label>
              <label className="block text-xs text-slate-300">Height {Math.round(rect.height * 100)}%
                <input className="w-full" type="range" min="5" max="100" value={Math.round(rect.height * 100)} onChange={(e) => setFreeSize('height', Number(e.target.value))} />
              </label>
            </>
          ) : (
            <label className="block text-xs text-slate-300">Crop size {Math.round(scale)}%
              <input className="w-full" type="range" min="10" max="100" value={Math.round(scale)} onChange={(e) => setScale(Number(e.target.value))} />
            </label>
          )}
        </div>
        <div className="space-y-3">
          <label className="block text-xs text-slate-300">Horizontal position
            <input className="w-full" type="range" min="0" max="100" disabled={rect.width >= 1} value={Math.round(horizontalPosition)} onChange={(e) => setPosition('x', Number(e.target.value))} />
          </label>
          <label className="block text-xs text-slate-300">Vertical position
            <input className="w-full" type="range" min="0" max="100" disabled={rect.height >= 1} value={Math.round(verticalPosition)} onChange={(e) => setPosition('y', Number(e.target.value))} />
          </label>
          <div>
            <label htmlFor="cropped-image-name" className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Copy name</label>
            <Input id="cropped-image-name" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
          </div>
        </div>
      </div>

      {imageSize.width > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Output width (px)
            <Input type="number" min={1} max={imageSize.width} value={outputWidth} onChange={(event) => setOutputPixels('width', Number(event.target.value))} disabled={saving} />
          </label>
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Output height (px)
            <Input type="number" min={1} max={imageSize.height} value={outputHeight} onChange={(event) => setOutputPixels('height', Number(event.target.value))} disabled={saving} />
          </label>
          <p className="col-span-2 text-[11px] text-slate-500">Output: {outputWidth} × {outputHeight} PNG</p>
        </div>
      )}
      {usedFallback && <p className="text-[11px] text-amber-300" role="status">The server crop route was unavailable, so the browser saved the cropped PNG through the compatible upload route.</p>}
      {error && <p className="text-xs text-red-400" role="alert">{error}</p>}
      <div className="flex justify-end gap-2 border-t border-white/10 pt-3">
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="button" variant="primary" size="sm" onClick={() => void handleSave()} disabled={saving || !imageUrl || !imageSize.width} loading={saving}>
          Save cropped copy
        </Button>
      </div>
    </Modal>
  );
};

export default CropImageModal;
